/* eslint-disable react/prop-types, react/destructuring-assignment */
import { Formik, useFormikContext } from 'formik'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { ModalPanel, ModalSidebar, TabSwitch } from '../../../../components/ModalSidebar'
import { ToastContext } from '../../../../context/ToastContext'
import { assignLicenseToEmployees, unassignLicenseFromEmployees, updateEmployee } from '../../../../crud/employees.crud'
import { updateUserProperties } from '../../../../crud/engage.crud'
import { createMgmTask } from '../../../../crud/integration.crud'
import { createHubTask, pollTaskStatus } from '../../../../crud/integrationHub.crud'
import useAllEmployees from '../../../../hooks/useAllEmployees'
import useCustomerLicenses from '../../../../hooks/useCustomerLicenses'
import useFreshEmployeeData from '../../../../hooks/useFreshEmployeeData'
import useGroupsFetch from '../../../../hooks/useGroupsFetch'
import CopyButton from '../../../../partials/content/CopyButton'
import { adaptGroupsToStandardFormat } from '../../../../util/groups.util'
import { TASK_ACTIONS, TASK_TYPES } from '../../../../util/hub-task-management/hub.constants'
import { getEmployeeFullName } from '../../../../util/table.util'
import { monitorTask } from '../../../../util/tasks.util'
import { getInitialValues, getValidationSchema, toApiPayload } from './formUtils'
import Groups from './Groups'
import Licenses from './Licenses'
import UserProperties from './UserProperties'
import { getEmployeeInitials, TABS } from './utils'

/** Employee fields compared to decide when to re-init form (job + address). */
const REINIT_COMPARE_FIELDS = [
  'department',
  'job_title',
  'companyName',
  'officeLocation',
  'employeeType',
  'hireDate',
  'streetAddress',
  'city',
  'state',
  'postalCode',
  'country',
]

const valueChanged = (a, b) => (a == null && b == null ? false : a !== b)

function EditEmployeeDataModal(props) {
  const {
    show,
    onHide,
    onUpdate,
    employee: tableRowEmployee,
    selectedCustomer,
    intl,
    headerBackButtonAction,
    allEmployeesGroups = [],
    allEmployees: propAllEmployees = [],
  } = props

  const [submitStatus, setSubmitStatus] = useState('idle')
  const [submitError, setSubmitError] = useState(null)
  const [formInitialValues, setFormInitialValues] = useState({})
  const lastEmployeeIdRef = useRef(null)
  const lastEmployeeDataRef = useRef(null)

  const [, setToastInfo] = useContext(ToastContext)
  const currentUser = useSelector(({ user }) => user.data)
  const selectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)

  const freshEmployee = useFreshEmployeeData({
    employee: tableRowEmployee,
    show,
    customer: selectedCustomer,
    isStaleData: (emp) => !(emp?.streetAddress || emp?.city || emp?.department || emp?.job_title),
  })
  const employee = useMemo(() => freshEmployee ?? {}, [freshEmployee])
  const currentEmployeeId = useMemo(
    () => employee?.id ?? employee?.employeeId ?? tableRowEmployee?.id ?? tableRowEmployee?.employeeId,
    [employee?.id, employee?.employeeId, tableRowEmployee?.id, tableRowEmployee?.employeeId],
  )

  const { allEmployees } = useAllEmployees({ allEmployees: propAllEmployees, selectedCustomer, show })
  const {
    customerLicenses,
    syncCustomerLicenses,
    isFetchingCustomerLicenses,
    errorFetchingCustomerLicenses,
    isSyncingCustomerLicenses,
  } = useCustomerLicenses({ customerId: selectedCustomer?.id, autoFetch: true })

  // Fetch groups if not provided via props
  const { fetchGroups, groups } = useGroupsFetch()

  const isOffice365 = selectedIntegration?.IntegrationApplication?.application_name === 'Office365'
  const allEmployeesGroupsFinal =
    allEmployeesGroups.length > 0 ? allEmployeesGroups : adaptGroupsToStandardFormat(groups)
  const isIntegrated = useMemo(
    () => !!(employee?.employeeIdentifier || employee?.employeeIntegrationInfo),
    [employee?.employeeIdentifier, employee?.employeeIntegrationInfo],
  )

  useEffect(() => {
    if (show && selectedCustomer?.id && allEmployeesGroups.length === 0 && isIntegrated) {
      fetchGroups({ customerId: selectedCustomer.id })
    }
  }, [show, selectedCustomer?.id, allEmployeesGroups.length, isIntegrated, fetchGroups])

  const modalTabs = useMemo(
    () => (isIntegrated ? TABS : [{ key: 'userproperties', label: 'User Properties' }]),
    [isIntegrated],
  )

  const employeeEmailOptions = useMemo(() => {
    if (!allEmployees?.length) return []
    return allEmployees
      .filter((emp) => {
        if (!emp || emp.ignored || !emp.email) return false
        const empId = emp.id || emp.employeeId
        return empId != null && Number(empId) !== Number(currentEmployeeId)
      })
      .map((emp) => {
        const empId = emp.id || emp.employeeId
        const fullName = getEmployeeFullName(emp)
        return {
          label: fullName ? `${fullName} (${emp.email})` : emp.email,
          value: Number(empId),
        }
      })
      .filter((opt) => opt.value != null && !Number.isNaN(opt.value))
      .sort((a, b) => a?.label?.localeCompare(b?.label))
  }, [allEmployees, currentEmployeeId])

  const customTitle = useMemo(
    () => (
      <div
        className="d-flex align-items-center employee-header-title"
        style={{
          gap: '1rem',
          height: '3.125rem',
          marginBottom: '1.5rem',
          boxSizing: 'border-box',
        }}
      >
        {/* Profile Picture / Avatar */}
        <div
          className="d-flex align-items-center justify-content-center employee-header-avatar fw-bold"
          style={{
            width: '3.125rem',
            height: '3.125rem',
            borderRadius: '50%',
            backgroundColor: 'var(--bs-primary)',
            color: 'var(--bs-white)',
            fontSize: '1.2rem',
            flexShrink: 0,
            boxSizing: 'border-box',
          }}
        >
          {getEmployeeInitials(employee)}
        </div>

        {/* Name and Email */}
        <div
          className="flex-grow"
          style={{
            minWidth: 0,
            height: '3.125rem',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxSizing: 'border-box',
          }}
        >
          <div className="d-flex align-items-center" style={{ height: '2rem', lineHeight: '2rem' }}>
            {(employee.isUserSigninEnabled === false || employee.enabled === false) && (
              <span className="suspended me-2" style={{ lineHeight: '2rem' }}>
                <i className="fa fa-ban" />
              </span>
            )}
            <span className="fw-bold" style={{ fontSize: '1.25rem', lineHeight: '2rem', display: 'inline-block' }}>
              {getEmployeeFullName(employee)}
            </span>
            {employee.ignored && (
              <span className="ms-2 badge badge-secondary" style={{ lineHeight: '2rem' }}>
                Ignored
              </span>
            )}
          </div>

          <div
            className="d-flex align-items-center"
            style={{
              gap: '0.5rem',
              height: '1.125rem',
              lineHeight: '1.125rem',
            }}
          >
            <span
              className="font-color-2"
              style={{ fontSize: '0.95rem', lineHeight: '1.125rem', display: 'inline-block' }}
            >
              {employee.email}
            </span>
            {employee.email && (
              <CopyButton textToCopy={employee.email} className="d-inline-block" style={{ lineHeight: '1.125rem' }} />
            )}
          </div>
        </div>
      </div>
    ),
    [employee],
  )

  useEffect(() => {
    if (!show) {
      lastEmployeeIdRef.current = null
      lastEmployeeDataRef.current = null
      setFormInitialValues({})
      setSubmitStatus('idle')
      setSubmitError(null)
      return
    }

    const sourceEmployee = employee && Object.keys(employee).length > 0 ? employee : tableRowEmployee || {}
    const prevEmployee = lastEmployeeDataRef.current
    const employeeIdChanged = currentEmployeeId != null && currentEmployeeId !== lastEmployeeIdRef.current
    const contactOrJobChanged =
      prevEmployee && REINIT_COMPARE_FIELDS.some((field) => valueChanged(sourceEmployee[field], prevEmployee[field]))
    const shouldReinit = employeeIdChanged || contactOrJobChanged || (!lastEmployeeIdRef.current && currentEmployeeId)

    if (shouldReinit && (currentEmployeeId != null || Object.keys(sourceEmployee).length > 0)) {
      setFormInitialValues(getInitialValues(sourceEmployee))
      lastEmployeeIdRef.current = currentEmployeeId
      lastEmployeeDataRef.current = REINIT_COMPARE_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field]: sourceEmployee[field] }),
        {},
      )
    }
  }, [show, employee, tableRowEmployee, currentEmployeeId])

  const initialValues = formInitialValues

  const validationSchema = useMemo(() => getValidationSchema(intl), [intl])

  const runHubTask = useCallback(
    async (actions, tasktype, action) => {
      try {
        const hubTask = {
          actions,
          tasktype,
          action,
          customerId: selectedCustomer?.id,
          organizationId: selectedCustomer?.organization,
          userId: currentUser?.id,
        }
        const taskResponse = await createHubTask(hubTask)
        const taskId = taskResponse?.data != null ? taskResponse.data.taskId : undefined
        if (taskId) await pollTaskStatus(taskId, { retries: 10, timeout: 3000 })
      } catch (error) {
        console.error('Error running hub task:', error)
        throw error
      }
    },
    [selectedCustomer?.id, selectedCustomer?.organization, currentUser?.id],
  )

  const finishSuccess = useCallback(() => {
    setSubmitStatus('success')
    setToastInfo({ show: true, type: 'GENERAL.ACTION_SUCCESS' })
    onUpdate?.()
    onHide()
  }, [setToastInfo, onUpdate, onHide])

  const finishError = useCallback(
    (errorMessage) => {
      setSubmitError(errorMessage)
      setSubmitStatus('error')
      setToastInfo({ show: true, type: 'GENERAL.ACTION_FAILED' })
    },
    [setToastInfo],
  )

  const handleSubmit = async (values) => {
    try {
      setSubmitStatus('loading')
      setSubmitError(null)

      const isEmailEditable = isIntegrated ? !employee?.onPremisesSyncEnabled : true
      const payload = toApiPayload(values, initialValues, isIntegrated, { isEmailEditable })
      const hasSqlChanges = Object.keys(payload.sql).length > 0

      if (!isIntegrated) {
        if (Object.keys(payload.sql).length > 0) {
          await updateEmployee({ id: employee.id, ...payload.sql })
        }
        finishSuccess()
        return
      }

      const hasGraphApiChanges = payload.graphApiProperties && Object.keys(payload.graphApiProperties).length > 0

      if (hasSqlChanges) {
        await updateEmployee({ id: employee.id, ...payload.sql })
      }

      if (hasGraphApiChanges && employee.employeeIdentifier && selectedCustomer) {
        await updateUserProperties({
          customerId: selectedCustomer.id,
          organizationId: selectedCustomer.organization,
          userId: currentUser?.id,
          employeeIdentifier: employee.employeeIdentifier,
          userProperties: payload.graphApiProperties,
        })
      }

      if (payload.groupsChanges && employee.employeeIdentifier && selectedCustomer) {
        const { m365Groups, securityGroups } = payload.groupsChanges

        if (m365Groups && (m365Groups.toBeAdded.length > 0 || m365Groups.toBeRemoved.length > 0)) {
          await runHubTask(
            {
              employeeIdentifiers: [employee.employeeIdentifier],
              groupsInfo: { toBeAdded: m365Groups.toBeAdded, toBeRemoved: m365Groups.toBeRemoved },
            },
            TASK_TYPES.manageM365Groups,
            TASK_ACTIONS.update,
          )
        }
        if (securityGroups && (securityGroups.toBeAdded.length > 0 || securityGroups.toBeRemoved.length > 0)) {
          await runHubTask(
            {
              employeeIdentifiers: [employee.employeeIdentifier],
              groupsInfo: {
                toBeAdded: securityGroups.toBeAdded,
                toBeRemoved: securityGroups.toBeRemoved,
              },
            },
            TASK_TYPES.manageSecurityGroups,
            TASK_ACTIONS.update,
          )
        }
      }

      const licenseChanges = payload.licensesChanges
      const licenseDelta =
        licenseChanges && selectedCustomer
          ? (() => {
              const toAdd = (licenseChanges.toBeAdded || []).filter(
                (id) => !(licenseChanges.toBeRemoved || []).includes(id),
              )
              const toRemove = (licenseChanges.toBeRemoved || []).filter(
                (id) => !(licenseChanges.toBeAdded || []).includes(id),
              )
              return toAdd.length > 0 || toRemove.length > 0 ? { toAdd, toRemove } : null
            })()
          : null
      if (licenseDelta) {
        const { toAdd: licenseIdsToAdd, toRemove: licenseIdsToRemove } = licenseDelta
        const isM365License = (id) => typeof id === 'string' && id.includes('-')
        const m365ToAdd = licenseIdsToAdd.filter(isM365License)
        const m365ToRemove = licenseIdsToRemove.filter(isM365License)
        const moduleToAdd = licenseIdsToAdd.filter((id) => !isM365License(id))
        const moduleToRemove = licenseIdsToRemove.filter((id) => !isM365License(id))

        const licensePromises = []

        if (isOffice365 && (m365ToAdd.length > 0 || m365ToRemove.length > 0) && employee.employeeIdentifier) {
          licensePromises.push(
            (async () => {
              const taskPayload = {
                customerId: selectedCustomer.id,
                userId: currentUser?.id,
                integrationType: 'Office365',
                mgmTaskId: 'M365_SECURE_PARTIAL',
                parameters: {
                  taskName: 'M365_ADD_USER',
                  actions: {
                    employeeIdentifier: employee.employeeIdentifier,
                    firstName: employee.first_name || employee.firstName,
                    lastName: employee.last_name || employee.lastName,
                    displayName: employee.display_name || employee.displayName,
                    email: employee.email,
                    department: employee.department,
                    licenseInfo: {
                      toBeAdded: m365ToAdd,
                      toBeRemoved: m365ToRemove,
                    },
                  },
                },
              }
              const taskResponse = await createMgmTask(taskPayload)
              const taskId = taskResponse?.data != null ? taskResponse.data.taskId : undefined
              if (taskId) {
                const { error } = await monitorTask(selectedCustomer.id, taskId, 60)
                if (error) {
                  throw new Error(error.rawMessage || error.StatusMessage || 'License update failed')
                }
              }
              if (syncCustomerLicenses) {
                await syncCustomerLicenses({
                  customerId: selectedCustomer.id,
                  organizationId: selectedCustomer.organization,
                  userId: currentUser?.id,
                })
              }
            })(),
          )
        }

        if (moduleToAdd.length > 0 || moduleToRemove.length > 0) {
          licensePromises.push(
            (async () => {
              if (moduleToRemove.length > 0) {
                await Promise.all(
                  moduleToRemove.map((licenseId) =>
                    unassignLicenseFromEmployees(selectedCustomer, [employee.id], licenseId),
                  ),
                )
              }
              if (moduleToAdd.length > 0) {
                await assignLicenseToEmployees(selectedCustomer, [employee.id], moduleToAdd)
              }
            })(),
          )
        }

        if (licensePromises.length > 0) {
          try {
            await Promise.all(licensePromises)
          } catch (error) {
            console.error('Error updating licenses:', error)
            throw error
          }
        }
      }

      if (payload.powershellAccessChanged !== undefined && employee.employeeIdentifier && selectedCustomer) {
        await runHubTask(
          {
            employeeIdentifiers: [employee.employeeIdentifier],
            remotePowerShellEnabled: payload.powershellAccessChanged,
          },
          TASK_TYPES.managePowershellAccess,
          TASK_ACTIONS.update,
        )
      }

      finishSuccess()
    } catch (error) {
      console.error('Error updating employee:', error)
      finishError(error?.response?.data?.message || error?.message || 'GENERAL.ACTION_FAILED')
    }
  }

  const formKey = useMemo(() => currentEmployeeId ?? 'new', [currentEmployeeId])

  // eslint-disable-next-line react/no-unstable-nested-components
  const EditFormContent = React.memo(
    ({
      modalSubmitStatusProp,
      submitErrorProp,
      employeeEmailOptionsProp,
      modalTabsProp,
      employeeProp,
      intlProp,
      isIntegratedProp,
      allEmployeesGroupsProp,
      customerLicensesProp,
      isFetchingCustomerLicensesProp,
      errorFetchingCustomerLicensesProp,
      syncCustomerLicensesProp,
      isSyncingCustomerLicensesProp,
    }) => {
      const { submitForm } = useFormikContext()

      const handleFormSubmit = (e) => {
        e.preventDefault()
        submitForm()
      }

      return (
        <ModalPanel
          leftButton={modalSubmitStatusProp === 'error' ? 'back' : 'cancel'}
          rightButton={{
            label: 'GENERAL.SAVE',
            variant: 'primary',
            onClick: () => submitForm(),
            disabled: modalSubmitStatusProp === 'loading',
            id: 'edit-employee-data-submit-btn',
          }}
          tabs={modalTabsProp}
          style={{ marginTop: '1.5rem' }}
        >
          <form onSubmit={handleFormSubmit} noValidate style={{ display: 'contents' }}>
            {modalSubmitStatusProp === 'error' && (
              <div
                className="section px-4 py-3"
                style={{
                  backgroundColor: 'var(--bs-danger-bg-subtle)',
                  borderBottom: '1px solid var(--bs-danger-border-subtle)',
                }}
              >
                <div className="d-flex align-items-center">
                  <i
                    className="fas fa-circle-xmark"
                    style={{ fontSize: '1.5rem', color: 'var(--bs-danger)', marginRight: '1rem' }}
                  />
                  <div className="flex-grow">
                    <h5 className="mb-1 text-color-danger">
                      <FormattedMessage id="GENERAL.ACTION_FAILED" />
                    </h5>
                    <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                      {typeof submitErrorProp === 'string' && submitErrorProp.startsWith('GENERAL.') ? (
                        <FormattedMessage id={submitErrorProp} />
                      ) : (
                        submitErrorProp
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <TabSwitch tab="userproperties">
              <UserProperties
                employee={employeeProp}
                intl={intlProp}
                employeeEmailOptions={employeeEmailOptionsProp}
                isIntegrated={isIntegratedProp}
              />
            </TabSwitch>

            {isIntegratedProp && (
              <>
                <TabSwitch tab="groups">
                  <Groups employee={employeeProp} intl={intlProp} allEmployeesGroups={allEmployeesGroupsProp} />
                </TabSwitch>

                <TabSwitch tab="licenses">
                  <Licenses
                    employee={employeeProp}
                    intl={intlProp}
                    customerLicenses={customerLicensesProp}
                    isFetchingCustomerLicenses={isFetchingCustomerLicensesProp}
                    errorFetchingCustomerLicenses={errorFetchingCustomerLicensesProp}
                    syncCustomerLicenses={syncCustomerLicensesProp}
                    isSyncingCustomerLicenses={isSyncingCustomerLicensesProp}
                  />
                </TabSwitch>
              </>
            )}
          </form>
        </ModalPanel>
      )
    },
  )

  EditFormContent.displayName = 'EditFormContent'

  return (
    <Formik
      key={formKey}
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ isSubmitting }) => (
        <ModalSidebar
          show={show}
          onHide={onHide}
          size="lg"
          title={customTitle}
          isLoading={isSubmitting || submitStatus === 'loading'}
          headerBackButtonAction={headerBackButtonAction}
        >
          <EditFormContent
            modalSubmitStatusProp={submitStatus}
            submitErrorProp={submitError}
            employeeEmailOptionsProp={employeeEmailOptions}
            modalTabsProp={modalTabs}
            employeeProp={employee}
            intlProp={intl}
            isIntegratedProp={isIntegrated}
            allEmployeesGroupsProp={allEmployeesGroupsFinal}
            customerLicensesProp={customerLicenses}
            isFetchingCustomerLicensesProp={isFetchingCustomerLicenses}
            errorFetchingCustomerLicensesProp={errorFetchingCustomerLicenses}
            syncCustomerLicensesProp={syncCustomerLicenses}
            isSyncingCustomerLicensesProp={isSyncingCustomerLicenses}
          />
        </ModalSidebar>
      )}
    </Formik>
  )
}

export default injectIntl(EditEmployeeDataModal)
